export const DEVICE_COUNT_MIN = 0;
/** Server sanity bound (routes/shops.ts's DEVICE_COUNT_MAX) -- not a
 *  business rule, just keeps a fat-fingered entry from exploding the
 *  licence-fee math in GET /reports/revenue. */
export const DEVICE_COUNT_MAX = 1000;

export const DEVICE_COUNT_FIELDS = ['device_count_admin', 'device_count_teller', 'device_count_payout'] as const;
export type DeviceCountField = (typeof DEVICE_COUNT_FIELDS)[number];

export const DEVICE_COUNT_LABELS: Record<DeviceCountField, string> = {
  device_count_admin: 'Admin devices',
  device_count_teller: 'Teller devices',
  device_count_payout: 'Payout devices',
};

export interface DeviceCountFormState {
  device_count_admin: string;
  device_count_teller: string;
  device_count_payout: string;
}

export interface DeviceCounts {
  device_count_admin: number;
  device_count_teller: number;
  device_count_payout: number;
}

/** Defaults to '0' for each field -- matches the server's default (create
 *  with no counts given) and a shop with no counts recorded yet. */
export function initialDeviceCountForm(shop?: Readonly<DeviceCounts> | null): DeviceCountFormState {
  return {
    device_count_admin: String(shop?.device_count_admin ?? 0),
    device_count_teller: String(shop?.device_count_teller ?? 0),
    device_count_payout: String(shop?.device_count_payout ?? 0),
  };
}

function parseDeviceCountInt(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  return Number(value.trim());
}

/** Server range (routes/shops.ts): integer, 0-1000. */
export function validateDeviceCountField(field: DeviceCountField, value: string): string | null {
  const n = parseDeviceCountInt(value);
  if (n === null) return `${DEVICE_COUNT_LABELS[field]} must be a whole number.`;
  if (n < DEVICE_COUNT_MIN || n > DEVICE_COUNT_MAX) {
    return `${DEVICE_COUNT_LABELS[field]} must be between ${DEVICE_COUNT_MIN} and ${DEVICE_COUNT_MAX}.`;
  }
  return null;
}

/** First validation problem across all three fields, or null when submittable. */
export function validateDeviceCountForm(form: Readonly<DeviceCountFormState>): string | null {
  for (const field of DEVICE_COUNT_FIELDS) {
    const problem = validateDeviceCountField(field, form[field]);
    if (problem) return problem;
  }
  return null;
}

/** Parsed integer payload for the three fields -- callers spread this onto
 *  a CreateShopRequest/UpdateShopRequest. Only meaningful once
 *  validateDeviceCountForm has confirmed the form is valid. */
export function deviceCountPayload(form: Readonly<DeviceCountFormState>): DeviceCounts {
  return {
    device_count_admin: Number(form.device_count_admin),
    device_count_teller: Number(form.device_count_teller),
    device_count_payout: Number(form.device_count_payout),
  };
}

/** Total device count across all three types (E2.1 spec 7.6: the shop
 *  detail page shows counts + total). */
export function deviceCountTotal(shop: Readonly<DeviceCounts>): number {
  return shop.device_count_admin + shop.device_count_teller + shop.device_count_payout;
}
